import { useQuery } from '@apollo/client';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@newfront-insurance/core-ui/v2';
import { useState } from 'react';

import { NovaAnalyticsKpiCards } from './nova-analytics-kpis';
import { AskNovaAnalytics, NovaAnalyticsReviewPreviews } from './nova-analytics-previews';
import { EverySkillTable, UsageByPersonTable, InactivePeopleTable } from './nova-analytics-tables';
import { GET_NOVA_ANALYTICS_DASHBOARD, NOVA_ANALYTICS_INITIAL_PAGE } from '../../../../queries/nova-analytics';
import { useCanViewNovaAnalytics } from '../../hooks/use-can-view-nova-analytics';

const ALL_TEAMS = 'all-teams';

export function NovaAnalyticsSettings() {
  const canViewAnalytics = useCanViewNovaAnalytics();

  // Mount Apollo queries only after both the feature flag and role checks pass.
  return canViewAnalytics ? <NovaAnalyticsDashboard /> : null;
}

function NovaAnalyticsDashboard() {
  const [teamId, setTeamId] = useState(ALL_TEAMS);
  const scope = teamId === ALL_TEAMS ? null : { teamId };
  const { data, previousData, loading, error, refetch } = useQuery(GET_NOVA_ANALYTICS_DASHBOARD, {
    variables: { scope, page: NOVA_ANALYTICS_INITIAL_PAGE },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    context: { notifications: { preventErrorNotification: true } },
  });
  const kpis = data?.getNovaAnalyticsKpis;
  // Team options are unscoped; retain their labels while the selected team's dashboard loads.
  const teams = data?.getNovaAnalyticsTeams ?? previousData?.getNovaAnalyticsTeams ?? [];

  return (
    <section
      data-testid="nova-analytics-dashboard"
      className="@container flex w-full flex-col gap-6"
      aria-labelledby="nova-analytics-heading"
    >
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex flex-col gap-1">
          <h2 id="nova-analytics-heading" className="text-steel-500 text-lg leading-6 font-normal">
            Dashboard
          </h2>
          <p className="text-steel-400 text-sm leading-4">Who is using Nova, and which skills they are using.</p>
        </div>
        <Select value={teamId} onValueChange={setTeamId} disabled={loading || teams.length === 0}>
          <SelectTrigger data-testid="nova-analytics-team-select" aria-label="Team" className="w-[294px] max-w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem data-testid="nova-analytics-team-all" value={ALL_TEAMS}>
              All teams
            </SelectItem>
            {teams.map((team) => (
              <SelectItem data-testid="nova-analytics-team-option" key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      {error || (!loading && !data) ? (
        <div
          data-testid="nova-analytics-dashboard-error"
          role="alert"
          className="border-steel-200/50 text-steel-400 flex flex-col items-start gap-3 rounded-sm border p-4 text-sm"
        >
          <p>Dashboard couldn’t be loaded.</p>
          <Button
            data-testid="nova-analytics-dashboard-retry"
            variant="secondary"
            size="small"
            onClick={() => {
              void refetch().catch(() => undefined);
            }}
          >
            Retry dashboard
          </Button>
        </div>
      ) : (
        <>
          {loading || !kpis ? (
            <div
              role="status"
              aria-label="Loading usage metrics"
              className="grid grid-cols-1 gap-3 @min-[600px]:grid-cols-2"
            >
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-[93px] rounded-sm" />
              ))}
            </div>
          ) : (
            <>
              <div className="text-steel-400 flex flex-wrap items-center gap-2 text-xs leading-4">
                {kpis.isPreview ? (
                  <span className="bg-steel-50 rounded-sm px-2 py-1 font-semibold">Sample data</span>
                ) : null}
                <span>
                  As of <time dateTime={kpis.asOfDate}>{kpis.asOfDate}</time>
                </span>
              </div>
              <NovaAnalyticsKpiCards kpis={kpis} />
            </>
          )}
          <AskNovaAnalytics />
          {kpis?.isPreview && !loading ? (
            <NovaAnalyticsReviewPreviews key={teamId} teamId={scope?.teamId ?? null} />
          ) : null}
          <EverySkillTable key={`skills-${teamId}`} scope={scope} initialLoading={loading} />
          <UsageByPersonTable key={`people-${teamId}`} scope={scope} initialLoading={loading} />
          <InactivePeopleTable key={`inactive-${teamId}`} scope={scope} initialLoading={loading} />
        </>
      )}
    </section>
  );
}
